import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { alertActions } from "_store";
import { ModalPopup } from "_components";
import { ERR_MSG } from "constants/labels";
import {
  getBalancingCurtailmentAndGroupApi,
  getDrvForecastAttachmentsApi,
  postDrvForecastViewApi,
  postDrvForecastViewForecastApi,
  postDrvForecastSaveApi,
  postDrvForecastSendApi,
  postDrvForecastUploadAttachmentsApi,
  deleteDrvForecastAttachmentsApi,
  getDrvForecastDownloadFileApi,
} from "services/forecastDrv/forecastDrv.thunks";
import ForecastDrvTable from "./ForecastDrvTable";
import ForecastDrvFilter from "./ForecastDrvFilter";
import ForecastDrvDetail from "./ForecastDrvDetail";
import ScheduledInterruptionRow from "./ScheduledInterruptionRow";
import ForecastDrvUpload from "./ForecastDrvUpload";
import { formatThousands } from "./ForecastDrvUtils";

const SectionCard = ({ children, sx = {} }) => (
  <Box
    sx={{
      border: "1px solid #d6e4f0",
      borderRadius: "10px",
      p: 2,
      backgroundColor: "#FFFFFF",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const AlertCard = ({ children, sx = {} }) => (
  <Box
    sx={{
      borderRadius: "10px",
      p: 2,
      backgroundColor: "#FFFFFF",
      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.10)",
      height: "100%",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const AlertTitle = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 700,
      textTransform: "uppercase",
      color: "#1a3a5c",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const AlertBody = ({ children }) => (
  <Typography sx={{ fontSize: "0.85rem", color: "#1a3a5c", lineHeight: 1.6 }}>
    {children}
  </Typography>
);

const emptyRows = (count = 5) =>
  Array.from({ length: count }, () => ({
    forecastedTemp: "",
    totalDRV: "",
    cspFirm: "",
    cspInterruptible: "",
    wglSendout: "",
  }));

const siDefaults = (index) => ({
  [`siType_${index}`]: null,
  [`siBegin_${index}`]: null,
  [`siEnd_${index}`]: null,
});

const formatDateTime = (date) => {
  if (!date) return null;
  return dayjs(date).format("hh:mm A dddd MM-DD-YYYY");
};

const header = "Forecast DRV";

// ── Validation messages ─────────────────────────────────────────────────────
const BALANCING_BEGIN_REQUIRED_MSG =
  "Begin date is required when a curtailment type is selected.";
const SI_BEGIN_REQUIRED_MSG =
  "Begin date is required when an interruption type is selected.";
const LOCKED_DATE_RANGE_REQUIRED_MSG =
  "Locked Gas Day date range is required.";
const SAVE_VALIDATION_ALERT_MSG =
  "Please provide a Begin date for each selected curtailment or interruption type, and a Locked Gas Day date range.";

// ── Message builders ───────────────────────────────────────────────────────────

const buildMessageBalancingCurtailment = (curtailmentLabel, curtailmentExtraText) => {
  if (!curtailmentLabel) return null;
  const base = `All suppliers with interruptible loads will be under a ${curtailmentLabel}.`;
  return curtailmentExtraText ? `${base} ${curtailmentExtraText}` : base;
};

const buildMessageSchedInterruptible = (siEntries) => {
  if (!siEntries || siEntries.length === 0) return null;
  return siEntries
    .map(({ siType, siBegin, siEnd }) => {
      const beginStr = siBegin ? formatDateTime(siBegin) : "";
      const endStr = siEnd ? formatDateTime(siEnd) : "";
      const dateRange = endStr
        ? `BEGINNING Gas Day ${beginStr} through ${endStr}:`
        : `BEGINNING Gas Day ${beginStr} UNTIL FURTHER NOTICE:`;
      return `${dateRange} Interruptible Accounts within ${siType?.label} will be under an interruption.`;
    })
    .join("\n");
};

const CRITICAL_DAY_MESSAGE =
  "A GAS DAY IS DEEMED CRITICAL WHEN THE AVERAGE DAILY TEMPERATURE IS EXPECTED TO BE 32°F OR LOWER. " +
  "FOR ANY CRITICAL DAY, CSPs MUST SUPPLY THEIR FULL FIRM TRANSPORTATION VOLUMES OR INCUR PENALTIES PER TARIFF.";

// ── Joins a list of file names into a natural-language, Oxford-comma list
//    e.g. ["A"] -> "A", ["A","B"] -> "A and B",
//    ["A","B","C"] -> "A, B, and C" ──────────────────────────────────────────
const formatFileListWithAnd = (items) => {
  if (!items || items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const buildMessageDRVForecast = (attachmentFileNames, lockedStart, lockedEnd) => {
  const fileList = formatFileListWithAnd(attachmentFileNames ?? []);
  const lockedSentence =
    lockedStart && lockedEnd
      ? `Please be advised that the forecast below is locked for Gas Days ${dayjs(lockedStart).format("MMM D, YYYY")} through ${dayjs(lockedEnd).format("MMM D, YYYY")}. Let us know if adjustments to your loads become necessary.`
      : "";
  const deliveryPercentageSentence = fileList
    ? `For each gas day, the allowable delivery percentages by pipeline can be estimated using the ${fileList}, all of which are attached to this email.`
    : "For each gas day, the allowable delivery percentages by pipeline can be estimated using the attached delivery documentation.";
  return (
    "Delivering your DRVs in compliance with the Matrix and Storage Imbalance limits is mandatory. " +
    "Penalties will be assessed for noncompliance.\n\n" +
    "Below are the forecasted system send outs for the DRV period stated. " +
    deliveryPercentageSentence +
    (lockedSentence ? `\n\n${lockedSentence}` : "")
  );
};

const buildMessageFooter = (portalUrl) =>
  "All volumes stated above are in Dekatherms.\n" +
  `DRV is your Daily Required Volume for your customer pools located at: ${portalUrl ?? "https://myportal.washingtongas.com/"}\n` +
  "Your allowed pipeline delivery limits on the attached matrix are based on the forecasted Total DRV Sendout.\n" +
  "If you have any questions with regards to nominations and operations please reach out to WGDeliveryService@washgas.com";

const getCurtailmentExtraText = (label) => {
  if (!label) return null;
  const lower = label.toLowerCase();
  if (lower.includes("short")) {
    return "Suppliers must deliver no less than the posted DRVs for their interruptible pools. Penalties will be assessed on any shortfall of gas for your interruptible loads for the affected gas days in accordance with tariffs.";
  }
  if (lower.includes("long")) {
    return "Suppliers must deliver no more than the posted DRVs for their interruptible pools. Penalties will be assessed on any gas in excess of your posted interruptible DRV.";
  }
  return null;
};

const ForecastDrv = () => {
  const dispatch = useDispatch();

  // ── Derive isAdmin from Redux auth state (same pattern as Nav.jsx) ─────────
  const authUser = useSelector((x) => x.auth?.value);
  const user = authUser?.Data;
  const userAccess = user?.UserAccess;
  const portalId = sessionStorage.getItem("portalID");
  const isAdmin = userAccess?.some(
    (access) =>
      access.Role.toLowerCase().includes("admin") &&
      access.PortalId.toString() === (portalId?.toString() ?? "")
  );
  const [curtailmentOptions, setCurtailmentOptions] = useState([]);
  const [interruptionTypeOptions, setInterruptionTypeOptions] = useState([]);

  const [lockedDateRange, setLockedDateRange] = useState({ startDate: null, endDate: null });
  const [tableRows, setTableRows] = useState(emptyRows());

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Applied date-range filter — narrows the table's displayed window to a
  // sub-range within the admin-set Locked Date Range. Cleared (both null)
  // means "show the full Locked Date Range".
  const [filterRange, setFilterRange] = useState({ startDate: null, endDate: null });

  const [siCount, setSiCount] = useState(1);

  const [uploadMode, setUploadMode] = useState("emails_attachment");
  const [forecastFileName, setForecastFileName] = useState(null);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [defaultAttachments, setDefaultAttachments] = useState([]);

  const [postedAt, setPostedAt] = useState(
    dayjs().format("MMMM D, YYYY [at] h:mm A")
  );
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [optionsReady, setOptionsReady] = useState(false);

  // ── Tracks whether Save has been attempted, so required-field
  //    helper text only appears after the first click (and then stays live). ──
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // ── Dirty tracking for the split Save / Send Email buttons ────────────────
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showSaveFirstModal, setShowSaveFirstModal] = useState(false);
  const [showSendConfirmModal, setShowSendConfirmModal] = useState(false);
  const [dataLoadedTick, setDataLoadedTick] = useState(0);
  const savedSnapshotRef = useRef(null);
  const lastLoadedTickRef = useRef(-1);

  const pendingSiDataRef = useRef(null);

  const { control, watch, trigger, setValue } = useForm({
    defaultValues: {
      startDate: null,
      lockedStartDate: null,
      lockedEndDate: null,
      balancingCurtailment: null,
      balancingBegin: null,
      balancingEnd: null,
      uploadMode: "emails_attachment",
      ...siDefaults(0),
    },
  });

  const startDate = watch("startDate");

  const balancingCurtailmentId = watch("balancingCurtailment");
  const balancingBegin = watch("balancingBegin");
  const balancingEnd = watch("balancingEnd");

  const siTypeIds = Array.from({ length: siCount }, (_, i) => watch(`siType_${i}`));
  const siBegins = Array.from({ length: siCount }, (_, i) => watch(`siBegin_${i}`));
  const siEnds = Array.from({ length: siCount }, (_, i) => watch(`siEnd_${i}`));

  const balancingCurtailmentOption =
    curtailmentOptions.find((o) => o.value === balancingCurtailmentId) ?? null;

  const siTypeOptions = siTypeIds.map(
    (id) => interruptionTypeOptions.find((o) => o.value === id) ?? null
  );

  // ── Detect if "ALL GROUPS" is selected in any SI row ──────────────────────
  const hasAllGroups = siTypeOptions.some(
    (opt) => opt?.label?.toUpperCase() === "ALL GROUPS"
  );

  // ── Clear balancing curtailment fields when ALL GROUPS is selected ─────────
  useEffect(() => {
    if (hasAllGroups) {
      setValue("balancingCurtailment", null);
      setValue("balancingBegin", null);
      setValue("balancingEnd", null);
    }
  }, [hasAllGroups, setValue]);

  const filteredInterruptionTypeOptionsPerRow = Array.from({ length: siCount }, (_, i) => {
    const otherSelected = siTypeIds.filter((id, j) => j !== i && !!id);
    return interruptionTypeOptions.filter((o) => !otherSelected.includes(o.value));
  });

  // ── Required-field validation (derived live from current form/state values) ─
  // A Begin date becomes required as soon as its corresponding type dropdown
  // has a value, and the locked date range is always required. These are pure
  // derivations, so they automatically clear themselves the moment the admin
  // fills in the missing value.
  const balancingBeginMissing = !!balancingCurtailmentId && !balancingBegin;
  const siBeginMissing = Array.from(
    { length: siCount },
    (_, i) => !!siTypeIds[i] && !siBegins[i]
  );
  const lockedDateRangeMissing =
    !lockedDateRange?.startDate || !lockedDateRange?.endDate;
  const hasValidationErrors =
    balancingBeginMissing ||
    siBeginMissing.some(Boolean) ||
    lockedDateRangeMissing;

  // Only surface error styling/helper text after the first Save attempt.
  const showBalancingBeginError = hasAttemptedSave && balancingBeginMissing;
  const showSiBeginErrors = siBeginMissing.map((missing) => hasAttemptedSave && missing);
  const showLockedDateRangeError = hasAttemptedSave && lockedDateRangeMissing;

  // ── Dirty tracking ──────────────────────────────────────────────────────
  // Serializes every field that feeds the Save/Send payload. Compared against
  // the last-saved snapshot to decide whether the Save button should be
  // enabled, and whether the Send button needs to block with a "save first"
  // prompt. Re-baselined every time fetchDrvForecastView completes (see
  // dataLoadedTick) and again right after a successful Save.
  const buildEditableSnapshot = () =>
    JSON.stringify({
      curtailmentId: balancingCurtailmentId,
      balancingBegin: balancingBegin ? dayjs(balancingBegin).toISOString() : null,
      balancingEnd: balancingEnd ? dayjs(balancingEnd).toISOString() : null,
      lockedStart: lockedDateRange?.startDate ? dayjs(lockedDateRange.startDate).toISOString() : null,
      lockedEnd: lockedDateRange?.endDate ? dayjs(lockedDateRange.endDate).toISOString() : null,
      si: Array.from({ length: siCount }, (_, i) => ({
        type: siTypeIds[i] ?? null,
        begin: siBegins[i] ? dayjs(siBegins[i]).toISOString() : null,
        end: siEnds[i] ? dayjs(siEnds[i]).toISOString() : null,
      })),
      // Read via the visible window (tableRowIndexOffset), not the raw
      // tableRows array. Rows can land at negative/out-of-range indices once
      // the Locked Date Range is edited earlier than rowsAnchorDate (see
      // tableRowIndexOffset below) — JSON.stringify only serializes real
      // 0..length-1 indices, so an edit written to a negative index would
      // otherwise be invisible here and Save would never re-enable. This
      // mirrors how hasCriticalTemp and buildSubmitPayload's gasDayDetails
      // already read tableRows.
      tableRows:
        tableDisplayStartDate && tableDayCount > 0
          ? Array.from({ length: tableDayCount }, (_, i) => tableRows[tableRowIndexOffset + i] || null)
          : [],
    });

  useEffect(() => {
    const snapshot = buildEditableSnapshot();
    if (lastLoadedTickRef.current !== dataLoadedTick) {
      lastLoadedTickRef.current = dataLoadedTick;
      savedSnapshotRef.current = snapshot;
      setIsDirty(false);
      return;
    }
    setIsDirty(snapshot !== savedSnapshotRef.current);
  }, [
    balancingCurtailmentId, balancingBegin, balancingEnd,
    lockedDateRange, siCount,
    JSON.stringify(siTypeIds), JSON.stringify(siBegins), JSON.stringify(siEnds),
    tableRows, dataLoadedTick,
    filterRange, startDate,
  ]);

  const fetchBalancingCurtailmentAndGroup = async () => {
    try {
      const res = await dispatch(getBalancingCurtailmentAndGroupApi()).unwrap();
      const { BalancingCurtailmentDetails, DRVGroupDeatils } = res?.Data ?? {};

      const newCurtailmentOptions = (BalancingCurtailmentDetails ?? []).map((item) => ({
        label: item.BalancingCurtailmentName,
        value: item.BalancingCurtailmentID,
      }));

      const newInterruptionTypeOptions = (DRVGroupDeatils ?? []).map((item) => ({
        label: item.DRVGroupName,
        value: item.DRVGroupID,
      }));

      setCurtailmentOptions(newCurtailmentOptions);
      setInterruptionTypeOptions(newInterruptionTypeOptions);
      setOptionsReady(true);

      return { curtailmentOptions: newCurtailmentOptions, interruptionTypeOptions: newInterruptionTypeOptions };
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
      return null;
    }
  };

  const fetchDrvForecastAttachments = async () => {
    try {
      const res = await dispatch(getDrvForecastAttachmentsApi()).unwrap();
      const files = (res?.Data ?? []).map((item) => ({
        id: item.ID,
        name: item.FileName,
        url: item.Url,
        format: item.Format,
        size: item.Size,
        isDefault: true,
      }));
      setDefaultAttachments(files);
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    }
  };

  const fetchDrvForecastView = async (resolvedCurtailmentOptions, resolvedInterruptionTypeOptions) => {
    setIsViewLoading(true);
    try {
      const payload = { PostedOn: dayjs().format("YYYY-MM-DDTHH:mm:ss") };
      const res = await dispatch(postDrvForecastViewApi(payload)).unwrap();
      const data = res?.Data ?? {};

      const {
        FiveDayDRVForecastDetails: details = {},
        SchedInterruptionDetails: siDetails = [],
        FiveDayDRVForecastGasDayDetails: gasDayDetails = [],
      } = data;

      setPostedAt(dayjs().format("MMMM D, YYYY [at] h:mm A"));

      if (details.ForecastLockStart || details.ForecastLockEnd) {
        setLockedDateRange({
          startDate: details.ForecastLockStart ? dayjs(details.ForecastLockStart) : null,
          endDate: details.ForecastLockEnd ? dayjs(details.ForecastLockEnd) : null,
        });
      }

      if (details.BalancingCurtailmentName) {
        const matchedCurtailment = resolvedCurtailmentOptions.find(
          (o) => o.label === details.BalancingCurtailmentName
        ) ?? null;
        setValue("balancingCurtailment", matchedCurtailment?.value ?? null);
        setValue(
          "balancingBegin",
          details.BalancingCurtailmentStart ? dayjs(details.BalancingCurtailmentStart) : null
        );
        setValue(
          "balancingEnd",
          details.BalancingCurtailmentEnd ? dayjs(details.BalancingCurtailmentEnd) : null
        );
      }

      const targetSiCount = Math.max(siDetails.length, 1);

      const applySiValues = (entries, options) => {
        entries.forEach((entry, i) => {
          const matchedSiType = options.find(
            (o) => o.label === entry.DRVGroupName
          ) ?? null;
          setValue(`siType_${i}`, matchedSiType?.value ?? null);
          setValue(`siBegin_${i}`, entry.InterruptionStart ? dayjs(entry.InterruptionStart) : null);
          setValue(`siEnd_${i}`, entry.InterruptionEnd ? dayjs(entry.InterruptionEnd) : null);
        });
      };

      if (targetSiCount !== siCount) {
        pendingSiDataRef.current = {
          siDetails,
          resolvedInterruptionTypeOptions,
          targetSiCount,
        };
        setSiCount(targetSiCount);
      } else {
        applySiValues(siDetails, resolvedInterruptionTypeOptions);
      }

      if (gasDayDetails.length > 0) {
        const firstGasDay = dayjs(gasDayDetails[0].GasDay);
        setValue("startDate", firstGasDay);

        // Numeric fields (except Forecasted Temp) get thousands separators
        // applied as soon as they come back from the API, e.g. 1000 -> "1,000".
        const mappedRows = gasDayDetails.map((day) => ({
          forecastedTemp: day.ForecastedTemp !== undefined ? String(day.ForecastedTemp) : "",
          totalDRV: day.TotalDRVSendOut !== undefined ? formatThousands(day.TotalDRVSendOut) : "",
          cspFirm: day.CSPFirm !== undefined ? formatThousands(day.CSPFirm) : "",
          cspInterruptible: day.CSPInterruptible !== undefined ? formatThousands(day.CSPInterruptible) : "",
          wglSendout: day.WGLSendOut !== undefined ? formatThousands(day.WGLSendOut) : "",
        }));

        // A single locked day is padded out to 5 rows; any locked range
        // longer than 1 day keeps exactly the rows the API returned.
        const lockedDayCountFromDetails =
          details.ForecastLockStart && details.ForecastLockEnd
            ? dayjs(details.ForecastLockEnd).diff(dayjs(details.ForecastLockStart), "day") + 1
            : mappedRows.length;

        if (lockedDayCountFromDetails === 1) {
          while (mappedRows.length < 5) {
            mappedRows.push({
              forecastedTemp: "", totalDRV: "", cspFirm: "", cspInterruptible: "", wglSendout: "",
            });
          }
        }

        setTableRows(mappedRows);
      }
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    } finally {
      setIsViewLoading(false);
      setDataLoadedTick((t) => t + 1);
      setFilterRange({ startDate: null, endDate: null });
    }
  };

  useEffect(() => {
    fetchBalancingCurtailmentAndGroup();
    fetchDrvForecastAttachments();
  }, []);

  useEffect(() => {
    if (optionsReady) {
      fetchDrvForecastView(curtailmentOptions, interruptionTypeOptions);
    }
  }, [optionsReady]);

  useEffect(() => {
    if (!pendingSiDataRef.current) return;

    const { siDetails, resolvedInterruptionTypeOptions, targetSiCount } = pendingSiDataRef.current;

    if (siCount !== targetSiCount) return;

    pendingSiDataRef.current = null;

    siDetails.forEach((entry, i) => {
      const matchedSiType = resolvedInterruptionTypeOptions.find(
        (o) => o.label === entry.DRVGroupName
      ) ?? null;
      setValue(`siType_${i}`, matchedSiType?.value ?? null);
      setValue(`siBegin_${i}`, entry.InterruptionStart ? dayjs(entry.InterruptionStart) : null);
      setValue(`siEnd_${i}`, entry.InterruptionEnd ? dayjs(entry.InterruptionEnd) : null);
    });
  }, [siCount]);

  useEffect(() => {
    if (!balancingCurtailmentId) {
      setValue("balancingBegin", null);
      setValue("balancingEnd", null);
    }
  }, [balancingCurtailmentId, setValue]);

  // ── DRV Forecast table date range ──────────────────────────────────────────
  // The table shows one row per day in the admin-set Locked Date Range. The
  // filter lets any user view any date range, including outside the locked
  // range — what gets saved/sent always matches whatever the table is
  // currently showing (locked range, or the Filter's narrower window), see
  // buildSubmitPayload below.
  const lockedDayCount =
    lockedDateRange?.startDate && lockedDateRange?.endDate
      ? dayjs(lockedDateRange.endDate).diff(dayjs(lockedDateRange.startDate), "day") + 1
      : 0;

  // A single locked day is padded out to 5 days; any locked range longer
  // than 1 day shows exactly those days, with no padding.
  const displayDayCount = lockedDayCount === 1 ? 5 : lockedDayCount;

  // The filter is not constrained to the Locked Date Range — whatever range
  // the user picks is honored directly, even if it falls outside it.
  const hasFilterRange = !!(filterRange.startDate && filterRange.endDate);

  const tableDayCount = hasFilterRange
    ? dayjs(filterRange.endDate).diff(dayjs(filterRange.startDate), "day") + 1
    : displayDayCount;

  const tableDisplayStartDate = hasFilterRange
    ? filterRange.startDate
    : (lockedDateRange?.startDate ?? startDate ?? null);

  // The date tableRows[0] actually represents — set once when tableRows is
  // (re)populated (initial load or a parsed upload) and left untouched when
  // the admin later edits the Locked Date Range, so each row keeps tracking
  // the same calendar day it was entered against.
  const rowsAnchorDate = startDate ?? lockedDateRange?.startDate ?? null;

  // Absolute row index (relative to rowsAnchorDate) that the displayed
  // window's first visible row corresponds to — this is what makes a row's
  // data follow its real calendar day rather than its position, whether
  // narrowed by the Filter or shifted by editing the Locked Date Range.
  // Can be negative or exceed tableRows.length when the displayed window
  // falls outside the data's actual range; ForecastDrvTable/handleRowChange
  // tolerate that by rendering/writing blank cells.
  const tableRowIndexOffset = tableDisplayStartDate && rowsAnchorDate
    ? dayjs(tableDisplayStartDate).diff(dayjs(rowsAnchorDate), "day")
    : 0;

  // Checked against the currently visible window (not the raw tableRows
  // array) because rows can live at negative/out-of-range indices once the
  // Locked Date Range is edited away from rowsAnchorDate (see above) —
  // Array.prototype.some only walks real 0..length-1 indices and would miss
  // those. This mirrors how buildSubmitPayload reads the visible window below.
  const hasCriticalTemp =
    tableDisplayStartDate && tableDayCount > 0
      ? Array.from({ length: tableDayCount }, (_, i) => tableRows[tableRowIndexOffset + i]).some(
          (row) => row?.forecastedTemp !== "" && Number(row?.forecastedTemp) <= 32
        )
      : false;

  const curtailmentLabel = balancingCurtailmentOption?.label ?? null;
  // Hide curtailment card when ALL GROUPS is selected
  const showCurtailmentCard = !!(curtailmentLabel && balancingBegin) && !hasAllGroups;

  const siEntries = Array.from({ length: siCount }, (_, i) => ({
    siType: siTypeOptions[i],
    siBegin: siBegins[i],
    siEnd: siEnds[i],
  })).filter(({ siType, siBegin }) => siType && siBegin);

  const showInterruptionCard = siEntries.length > 0;
  const showCriticalDayCard = hasCriticalTemp;

  const curtailmentExtraText = getCurtailmentExtraText(curtailmentLabel);

 // ── Save / Send payload builder ─────────────────────────────────────────────
  const buildSubmitPayload = () => {
      const messageBalancingCurtailment = showCurtailmentCard
        ? buildMessageBalancingCurtailment(curtailmentLabel, curtailmentExtraText)
        : null;

      const messageSchedInterruptible = showInterruptionCard
        ? buildMessageSchedInterruptible(siEntries)
        : null;

      const messageCriticalDay = showCriticalDayCard ? CRITICAL_DAY_MESSAGE : null;

      const attachmentFileNames = defaultAttachments.map((f) => f.name);
      const messageDRVForecast = buildMessageDRVForecast(
        attachmentFileNames,
        lockedDateRange?.startDate,
        lockedDateRange?.endDate
      );

      const messageFooter = buildMessageFooter();

      const fiveDayDetails = {
        PostedOn: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
        ForecastLockStart: lockedDateRange?.startDate
          ? dayjs(lockedDateRange.startDate).format("YYYY-MM-DDTHH:mm:ss")
          : null,
        ForecastLockEnd: lockedDateRange?.endDate
          ? dayjs(lockedDateRange.endDate).format("YYYY-MM-DDTHH:mm:ss")
          : null,
        BalancingCurtailmentName: curtailmentLabel ?? null,
        BalancingCurtailmentStart: balancingBegin
          ? dayjs(balancingBegin).format("YYYY-MM-DDTHH:mm:ss")
          : null,
        BalancingCurtailmentEnd: balancingEnd
          ? dayjs(balancingEnd).format("YYYY-MM-DDTHH:mm:ss")
          : null,
      };

      const schedInterruptionDetails = siEntries.map(({ siType, siBegin, siEnd }) => ({
        DRVGroupName: siType?.label ?? null,
        InterruptionStart: siBegin ? dayjs(siBegin).format("YYYY-MM-DDTHH:mm:ss") : null,
        InterruptionEnd: siEnd ? dayjs(siEnd).format("YYYY-MM-DDTHH:mm:ss") : null,
      }));

      // Gas Days span exactly what the table currently shows (locked range,
      // or the Filter's narrower window). Forecasted Temp is free text; it
      // and the other 4 fields are sent as strings straight from table state
      // (the other 4 comma-formatted, e.g. "1,000").
      const gasDayDetails =
        tableDisplayStartDate && tableDayCount > 0
          ? Array.from({ length: tableDayCount }, (_, i) => {
              const row = tableRows[tableRowIndexOffset + i] || {};
              return {
                GasDay: dayjs(tableDisplayStartDate).add(i, "day").format("YYYY-MM-DDTHH:mm:ss"),
                ForecastedTemp: row.forecastedTemp !== undefined && row.forecastedTemp !== "" ? row.forecastedTemp : "0",
                TotalDRVSendOut: row.totalDRV !== undefined && row.totalDRV !== "" ? row.totalDRV : "0",
                CSPFirm: row.cspFirm !== undefined && row.cspFirm !== "" ? row.cspFirm : "0",
                CSPInterruptible: row.cspInterruptible !== undefined && row.cspInterruptible !== "" ? row.cspInterruptible : "0",
                WGLSendOut: row.wglSendout !== undefined && row.wglSendout !== "" ? row.wglSendout : "0",
              };
            })
          : [];

      return {
        MessageBalancingCurtailment: messageBalancingCurtailment,
        MessageSchedInterruptible: messageSchedInterruptible,
        MessageCriticalDay: messageCriticalDay,
        MessageDRVForecast: messageDRVForecast,
        MessageFooter: messageFooter,
        FiveDayDRVForecastDetails: fiveDayDetails,
        SchedInterruptionDetails: schedInterruptionDetails,
        FiveDayDRVForecastGasDayDetails: gasDayDetails,
      };
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setHasAttemptedSave(true);

    if (hasValidationErrors) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildSubmitPayload();
      const res = await dispatch(postDrvForecastSaveApi(payload)).unwrap();
      dispatch(alertActions.success({ message: res?.Message, header }));
      savedSnapshotRef.current = buildEditableSnapshot();
      setIsDirty(false);
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    } finally {
      setIsSaving(false);
    }
  };

  // ── Send Email ───────────────────────────────────────────────────────────
  const handleSendClick = () => {
    if (isDirty) {
      setShowSaveFirstModal(true);
      return;
    }
    setShowSendConfirmModal(true);
  };

  const handleSendConfirmed = async () => {
    setShowSendConfirmModal(false);
    setIsSending(true);
    try {
      const payload = buildSubmitPayload();
      const res = await dispatch(postDrvForecastSendApi(payload)).unwrap();
      dispatch(alertActions.success({ message: res?.Message, header }));
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    } finally {
      setIsSending(false);
    }
  };

  // ── Shared fetch+merge for a given [rangeStart, rangeEnd] window ───────────
  // Calls drv-forecast/view-forecast for the range and merges the returned
  // gas-day figures into tableRows at the correct offset from rowsAnchorDate.
  // Used by both the table Filter and the Lock Date Range picker, since
  // changing either should refresh the table with real data for the newly
  // selected range. Returns true/false to indicate success.
  const fetchAndMergeForecastRange = async (rangeStart, rangeEnd) => {
    if (!rangeStart || !rangeEnd) return false;

    dispatch(alertActions.clear());
    setIsViewLoading(true);
    try {
      const res = await dispatch(
        postDrvForecastViewForecastApi({
          StartDate: rangeStart,
          EndDate: rangeEnd,
        })
      ).unwrap();

      const gasDayDetails = res?.Data ?? [];

      if (gasDayDetails.length > 0 && rowsAnchorDate) {
        setTableRows((prev) => {
          const updated = Object.assign([], prev);
          gasDayDetails.forEach((day) => {
            const offset = dayjs(day.GasDay).diff(dayjs(rowsAnchorDate), "day");
            while (updated.length <= offset) {
              updated.push({
                forecastedTemp: "", totalDRV: "", cspFirm: "", cspInterruptible: "", wglSendout: "",
              });
            }
            updated[offset] = {
              forecastedTemp: day.ForecastedTemp !== undefined ? String(day.ForecastedTemp) : "",
              totalDRV: day.TotalDRVSendOut !== undefined ? formatThousands(day.TotalDRVSendOut) : "",
              cspFirm: day.CSPFirm !== undefined ? formatThousands(day.CSPFirm) : "",
              cspInterruptible: day.CSPInterruptible !== undefined ? formatThousands(day.CSPInterruptible) : "",
              wglSendout: day.WGLSendOut !== undefined ? formatThousands(day.WGLSendOut) : "",
            };
          });
          return updated;
        });
      }

      return true;
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
      return false;
    } finally {
      setIsViewLoading(false);
    }
  };

  const handleFilter = async (data) => {
    if (!data.StartDate || !data.EndDate) return;
    const success = await fetchAndMergeForecastRange(data.StartDate, data.EndDate);
    if (success) setFilterRange({ startDate: data.StartDate, endDate: data.EndDate });
  };

  // startDate is left untouched here — it's the anchor for what date
  // tableRows[0] actually holds data for, and must survive a Locked Date
  // Range edit so each row keeps following its real calendar day (see
  // rowsAnchorDate above). filterRange is cleared since it was set relative
  // to whatever window was showing before this change. The new locked range
  // is then re-fetched from view-forecast so the table reflects real data
  // for it right away.
  const handleLockedDateRangeChange = async (range) => {
    setLockedDateRange(range);
    setFilterRange({ startDate: null, endDate: null });
    await fetchAndMergeForecastRange(range?.startDate, range?.endDate);
  };

  const handleRowChange = (rowIndex, field, value) => {
    setTableRows((prev) => {
      const updated = Object.assign([], prev);
      updated[rowIndex] = { ...updated[rowIndex], [field]: value };
      return updated;
    });
  };

  const handleTableRowsReset = () => setTableRows(emptyRows(displayDayCount || 5));

  // Applies a fully-parsed DRV Forecast Table upload in one pass: startDate,
  // lockedDateRange, and tableRows are all set together so the visible
  // window (driven primarily by lockedDateRange, see tableDisplayStartDate/
  // tableRowIndexOffset above) lines up with the uploaded data on the very
  // next render — the uploaded date range becomes the new Locked Date Range.
  const handleTableUpload = ({ startDate: uploadedStartDate, rows }) => {
    setValue("startDate", uploadedStartDate);
    setLockedDateRange({
      startDate: uploadedStartDate,
      endDate: uploadedStartDate.add(rows.length - 1, "day"),
    });
    setFilterRange({ startDate: null, endDate: null });
    setTableRows(rows);
  };

  const handleUploadModeChange = (newMode) => setUploadMode(newMode);

  const addScheduledInterruption = () => {
    const newIndex = siCount;
    setValue(`siType_${newIndex}`, null);
    setValue(`siBegin_${newIndex}`, null);
    setValue(`siEnd_${newIndex}`, null);
    setSiCount((prev) => prev + 1);
  };

  const removeScheduledInterruption = (index) => {
    if (siCount <= 1) return;
    for (let i = index; i < siCount - 1; i++) {
      setValue(`siType_${i}`, watch(`siType_${i + 1}`));
      setValue(`siBegin_${i}`, watch(`siBegin_${i + 1}`));
      setValue(`siEnd_${i}`, watch(`siEnd_${i + 1}`));
    }
    setValue(`siType_${siCount - 1}`, null);
    setValue(`siBegin_${siCount - 1}`, null);
    setValue(`siEnd_${siCount - 1}`, null);
    setSiCount((prev) => prev - 1);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {isViewLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(2px)",
          }}
        >
          <CircularProgress size={48} sx={{ color: "#1a3a5c" }} />
        </Box>
      )}

      <Box sx={{ minHeight: "100vh", py: 3, opacity: isViewLoading ? 0.5 : 1, transition: "opacity 0.2s" }}>

        <Grid container spacing={2} sx={{ mb: 2 }}>

          {showCurtailmentCard && (
            <Grid item xs={12} sm={6} lg={3}>
              <AlertCard sx={{ backgroundColor: "#ffe0e0" }}>
                <AlertTitle>
                  OPERATIONAL FLOW ORDER (OFO) - SYSTEM ALERT - {curtailmentLabel}
                </AlertTitle>
                <AlertBody>
                  BEGINNING GAS DAY{" "}
                  <strong>{formatDateTime(balancingBegin)}</strong>
                  {balancingEnd ? (
                    <> THROUGH <strong>{formatDateTime(balancingEnd)}</strong></>
                  ) : (
                    " UNTIL FURTHER NOTICE"
                  )}
                  . All suppliers with interruptible loads will be under a{" "}
                  <strong>{curtailmentLabel}</strong>.
                </AlertBody>
                {curtailmentExtraText && (
                  <AlertBody sx={{ mt: 1 }}>
                    {curtailmentExtraText}
                  </AlertBody>
                )}
              </AlertCard>
            </Grid>
          )}

          {showInterruptionCard && (
            <Grid item xs={12} sm={6} lg={3}>
              <AlertCard sx={{ backgroundColor: "#ffe0e0" }}>
                <AlertTitle>OPERATIONAL FLOW ORDER (OFO) - SYSTEM ALERT - INTERRUPTIONS:</AlertTitle>
                {siEntries.map(({ siType, siBegin, siEnd }, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <Divider sx={{ borderColor: "#d6e4f0", my: 1 }} />}
                    <AlertBody>
                      BEGINNING Gas Day <strong>{formatDateTime(siBegin)}</strong>{" "}
                      {siEnd ? (
                        <>through <strong>{formatDateTime(siEnd)}</strong></>
                      ) : (
                        "UNTIL FURTHER NOTICE"
                      )}
                      : Interruptible Accounts within{" "}
                      <strong>{siType?.label}</strong>{" "}
                      will be under an interruption.
                    </AlertBody>
                  </React.Fragment>
                ))}
              </AlertCard>
            </Grid>
          )}

          {showCriticalDayCard && (
            <Grid item xs={12} sm={6} lg={3}>
              <AlertCard sx={{ backgroundColor: "#ffe0e0" }}>
                <AlertTitle>Critical Day Notice</AlertTitle>
                <AlertBody>
                  CRITICAL DAY: WGL IS ISSUING A CRITICAL DAY(S) NOTICE BASED
                  ON FORECASTED TEMPERATURES. A GAS DAY IS DEEMED CRITICAL WHEN
                  THE AVERAGE DAILY TEMPERATURE IS EXPECTED TO BE 32°F OR LOWER.
                  FOR ANY CRITICAL DAY, CSPs MUST SUPPLY THEIR FULL FIRM
                  TRANSPORTATION VOLUMES OR INCUR PENALTIES PER TARIFF.
                </AlertBody>
              </AlertCard>
            </Grid>
          )}

          <Grid item xs={12} sm={6} lg={3}>
            <AlertCard>
              <AlertTitle>WGL DRV Forecast</AlertTitle>
              <AlertBody>
                Delivering your DRVs in compliance with the
                Matrix and Storage Imbalance limits is mandatory. Penalties will
                be assessed for noncompliance.
                <br /><br />
                Below are the forecasted system send outs for the DRV period
                stated.{" "}
                {defaultAttachments.length > 0 ? (
                  <>
                    For each gas day, the allowable delivery percentages by
                    pipeline can be estimated using the{" "}
                    {formatFileListWithAnd(defaultAttachments.map((f) => f.name))}
                    , all of which are attached to this email.
                  </>
                ) : (
                  "For each gas day, the allowable delivery percentages by pipeline can be estimated using the attached delivery documentation."
                )}
              </AlertBody>
              {lockedDateRange?.startDate && lockedDateRange?.endDate && (
                <AlertBody sx={{ mt: 1 }}>
                  <br />
                  Please be advised that the forecast below is locked for Gas Days{" "}
                  <strong>{dayjs(lockedDateRange.startDate).format("MMM D, YYYY")}</strong>{" "}
                  through{" "}
                  <strong>{dayjs(lockedDateRange.endDate).format("MMM D, YYYY")}</strong>.{" "}
                  Let us know if adjustments to your loads become necessary.
                </AlertBody>
              )}
            </AlertCard>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <AlertCard>
              <AlertBody>
                An interruption requires the affected group of customers to stop use of natural gas and switch to their alternate source of fuel.
                Please inform your interruptible customers who fall within the stated groups of this notice as soon as possible.
                Penalties will be assessed to customers who violate the order.
                Please provide your interruptible customers to prepare for this interruption schedule.
              </AlertBody>
            </AlertCard>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <ForecastDrvDetail
            postedAt={postedAt}
            lockedDateRange={lockedDateRange}
            onLockedDateRangeChange={handleLockedDateRangeChange}
            isAdmin={isAdmin}
            dateRangeError={showLockedDateRangeError}
            dateRangeHelperText={
              showLockedDateRangeError ? LOCKED_DATE_RANGE_REQUIRED_MSG : undefined
            }
          />
        </Box>

        <Grid container spacing={2} alignItems="flex-start">

          <Grid item xs={12} md={8}>
            <SectionCard sx={{ mb: 2 }}>
              {Array.from({ length: siCount }, (_, index) => (
                <React.Fragment key={index}>
                  <ScheduledInterruptionRow
                    index={index}
                    siCount={siCount}
                    isLast={index === siCount - 1}
                    control={control}
                    trigger={trigger}
                    watch={watch}
                    setValue={setValue}
                    balancingCurtailment={balancingCurtailmentId}
                    siTypes={siTypeIds}
                    onAdd={addScheduledInterruption}
                    onRemove={() => removeScheduledInterruption(index)}
                    curtailmentOptions={curtailmentOptions}
                    interruptionTypeOptions={filteredInterruptionTypeOptionsPerRow[index]}
                    isAdmin={isAdmin}
                    hasAllGroups={hasAllGroups}
                    balancingBeginError={showBalancingBeginError}
                    balancingBeginHelperText={
                      showBalancingBeginError ? BALANCING_BEGIN_REQUIRED_MSG : undefined
                    }
                    siBeginError={showSiBeginErrors[index]}
                    siBeginHelperText={
                      showSiBeginErrors[index] ? SI_BEGIN_REQUIRED_MSG : undefined
                    }
                  />
                  {index < siCount - 1 && (
                    <Divider sx={{ borderColor: "#d6e4f0", my: 1.5 }} />
                  )}
                </React.Fragment>
              ))}
            </SectionCard>

            <SectionCard sx={{ backgroundColor: "#fff" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontWeight: 700, color: "#1a3a5c", mb: 1 }}>
                  DRV Forecast Table
                </Typography>
                <ForecastDrvFilter
                  handleFilter={handleFilter}
                  startDate={filterRange.startDate}
                  endDate={filterRange.endDate}
                  minDate={lockedDateRange?.startDate ?? null}
                  maxDate={lockedDateRange?.endDate ?? null}
                  disabled={!lockedDateRange?.startDate || !lockedDateRange?.endDate}
                  isOpen={isFilterOpen}
                  onOpen={() => setIsFilterOpen(true)}
                  onClose={() => setIsFilterOpen(false)}
                />
              </Box>
              <ForecastDrvTable
                startDate={tableDisplayStartDate}
                dayCount={tableDayCount}
                rowIndexOffset={tableRowIndexOffset}
                rows={tableRows}
                onRowChange={handleRowChange}
                isAdmin={isAdmin}
              />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <ForecastDrvUpload
              uploadMode={uploadMode}
              onUploadModeChange={handleUploadModeChange}
              setValue={setValue}
              watch={watch}
              control={control}
              trigger={trigger}
              siCount={siCount}
              setSiCount={setSiCount}
              tableRows={tableRows}
              onRowChange={handleRowChange}
              lockedDateRange={lockedDateRange}
              tableDisplayStartDate={tableDisplayStartDate}
              tableDayCount={tableDayCount}
              tableRowIndexOffset={tableRowIndexOffset}
              onTableUpload={handleTableUpload}
              onTableRowsReset={handleTableRowsReset}
              forecastFileName={forecastFileName}
              onForecastFileNameChange={setForecastFileName}
              emailAttachments={emailAttachments}
              onEmailAttachmentsChange={setEmailAttachments}
              defaultAttachments={defaultAttachments}
              onDefaultAttachmentsChange={setDefaultAttachments}
              curtailmentOptions={curtailmentOptions}
              interruptionTypeOptions={interruptionTypeOptions}
              dispatch={dispatch}
              uploadAttachmentsApi={postDrvForecastUploadAttachmentsApi}
              deleteAttachmentsApi={deleteDrvForecastAttachmentsApi}
              fetchAttachmentsApi={getDrvForecastAttachmentsApi}
              downloadFileApi={getDrvForecastDownloadFileApi}
              isAdmin={isAdmin}
            />
          </Grid>
        </Grid>

        {/* Save / Send Email hidden when isAdmin is false */}
        {isAdmin && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isDirty || isViewLoading || isSaving}
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                backgroundColor: "#1a3a5c",
                "&:hover": { backgroundColor: "#1976d2" },
              }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              onClick={handleSendClick}
              disabled={isViewLoading || isSending}
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                backgroundColor: "#1a3a5c",
                "&:hover": { backgroundColor: "#1976d2" },
              }}
            >
              Send Email
            </Button>
          </Box>
        )}

        {showSaveFirstModal && (
          <ModalPopup
            header="Send Email"
            message1="Changes have been made to the System DRV & OFO data. Please click Save before using the Send Email function."
            btnPrimaryText="OK"
            handlePrimaryClick={() => setShowSaveFirstModal(false)}
          />
        )}

        {showSendConfirmModal && (
          <ModalPopup
            header="Send Email"
            message1="Are you sure you want to send the DRV & OFO notification email?"
            btnPrimaryText="Confirm"
            btnSecondaryText="Cancel"
            handlePrimaryClick={handleSendConfirmed}
            handleSecondaryClick={() => setShowSendConfirmModal(false)}
          />
        )}

        <AlertCard sx={{ mt: 2, backgroundColor: "#FFFFFF" }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#1a3a5c", lineHeight: 1.6 }}>
            All volumes stated above are in Dekatherms.
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#1a3a5c", lineHeight: 1.6 }}>
            DRV is your Daily Required Volume for your customer pools located at:{" "}
            <a
              href="https://myportal.washingtongas.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2" }}
            >
              https://myportal.washingtongas.com/
            </a>
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#1a3a5c", lineHeight: 1.6 }}>
            **Your allowed pipeline delivery limits on the attached matrix are based on the forecasted Total DRV Sendout.
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#1a3a5c", lineHeight: 1.6 }}>
            If you have any questions with regards to nominations and operations please reach out to{" "}
            <a href="mailto:WGDeliveryService@washgas.com" style={{ color: "#1976d2" }}>
              WGDeliveryService@washgas.com
            </a>
          </Typography>
        </AlertCard>
      </Box>
    </LocalizationProvider>
  );
};

export default ForecastDrv;